import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import AssetService from '../../../Services/assets.service';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps, xtramTextAlign } from './AssetModalShell/assetModalInputs';

const EditAssetGroup = ({ open, onClose, caseId, assetGroup, refreshAssetGroups, onDelete, token }) => {

    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n); 
    const [editedAssetGroup, setEditedAssetGroup] = useState({
        caseId: caseId || "",
        assetGroupId: assetGroup?.assetGroupId || "",
        name: assetGroup?.name || "",
        description: assetGroup?.description || "",
        depreciationPercentage: assetGroup?.depreciationPercentage || "",
    });


    useEffect(() => {

        if (assetGroup) {
            setEditedAssetGroup(assetGroup);
        }
    }, [assetGroup, caseId]);

    const inputProps = xtramInputProps(direction);
    const inputStyle = xtramTextAlign(direction);

    const handleChange = (field, value) => {
        setEditedAssetGroup((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!editedAssetGroup.name || !editedAssetGroup.depreciationPercentage || !editedAssetGroup.assetGroupId) {
            toast.error(t('Please fill all required fields'));
            return;
        }

        const payload = {
            CaseId: caseId,
            AssetGroupId: editedAssetGroup.assetGroupId,
            Name: editedAssetGroup.name,
            Description: editedAssetGroup.description || "",
            DepreciationPercentage: parseFloat(editedAssetGroup.depreciationPercentage),
        };

        try {
            const response = await AssetService.UpdateAssetGroup(payload, token);
            if (response.success) {
                toast.success(t('Asset group updated successfully'));
                refreshAssetGroups();
                // onClose();
            } else {
                toast.error(response.error || t('Failed to update asset group'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('An error occurred while updating asset group'));
        }
    };

    const handleDelete = async () => {
        try {
            const response = await AssetService.DeleteAssetGroup(caseId, editedAssetGroup.assetGroupId, token);
            if (response.success) {
                toast.success(t('Asset group deleted successfully'));
                onDelete(editedAssetGroup.assetGroupId);
                onClose();
            } else {
                toast.error(response.error || t('Failed to delete asset group'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('An error occurred while deleting asset group'));
        }
    };

    if (!open) return null;

    const fields = [
        { key: 'assetGroupId', label: t('AssetGroupId'), value: editedAssetGroup.assetGroupId, required: true, type: 'number' },
        { key: 'name', label: t('AssetGroupName'), value: editedAssetGroup.name, required: true },
        { key: 'depreciationPercentage', label: t('DepreciationPercentage'), value: editedAssetGroup.depreciationPercentage, required: true, type: 'number' },
        { key: 'description', label: t('Description'), value: editedAssetGroup.description, full: true },
    ];

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('EditAssetGroup')}
            subtitle={editedAssetGroup.name ? `${editedAssetGroup.assetGroupId} - ${editedAssetGroup.name}` : null}
            direction={direction}
            size="sm"
            footerStart={(
                <button type="button" className={shell.dangerBtn} onClick={handleDelete}>
                    <DeleteIcon />
                    {t('Delete')}
                </button>
            )}
            footer={(
                <>
                    <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                        {t('Cancel')}
                    </button>
                    <button type="button" className={shell.primaryBtn} onClick={handleSave}>
                        <SaveIcon />
                        {t('Save')}
                    </button>
                </>
            )}
        >
            <div className={shell.fieldGrid}>
                {fields.map((f) => (
                    <div key={f.key} className={`${shell.field}${f.full ? ` ${shell.fieldFull}` : ''}`}>
                        <span className={shell.fieldLabel}>
                            <span>{f.required && <span className={shell.requiredMark}>*</span>}{f.label}</span>
                        </span>
                        <TextField
                            fullWidth
                            placeholder={f.label}
                            value={f.value ?? ''}
                            onChange={(e) => handleChange(f.key, e.target.value)}
                            type={f.type || 'text'}
                            variant="standard"
                            InputProps={inputProps}
                            inputProps={inputStyle}
                        />
                    </div>
                ))}
            </div>
        </AssetModalShell>
    );
};

export default EditAssetGroup;
